import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiPhone, FiUserCheck, FiUserX, FiClock } = FiIcons;

const PhoneSearch = ({ onSearch, searchResult, isLoading }) => {
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (phoneNumber.trim()) {
      onSearch(phoneNumber.trim());
    }
  };

  const formatPhone = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    
    // Format as: 0744 155 921
    if (cleaned.length >= 1) {
      let formatted = '';
      
      // Add first digit (0)
      if (cleaned.length >= 1) {
        formatted += cleaned.substring(0, 1);
      }
      
      // Add first group (3 digits after 0)
      if (cleaned.length >= 4) {
        formatted += cleaned.substring(1, 4);
      } else if (cleaned.length > 1) {
        formatted += cleaned.substring(1);
        return formatted;
      }
      
      // Add space and second group (3 digits)
      if (cleaned.length >= 7) {
        formatted += ' ' + cleaned.substring(4, 7);
      } else if (cleaned.length > 4) {
        formatted += ' ' + cleaned.substring(4);
        return formatted;
      }
      
      // Add space and third group (3 digits)
      if (cleaned.length >= 10) {
        formatted += ' ' + cleaned.substring(7, 10);
      } else if (cleaned.length > 7) {
        formatted += ' ' + cleaned.substring(7);
        return formatted;
      }
      
      // Add remaining digits if any
      if (cleaned.length > 10) {
        formatted += ' ' + cleaned.substring(10);
      }
      
      return formatted;
    }
    
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhoneNumber(formatted);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <SafeIcon icon={FiPhone} className="text-blue-500" />
        Phone Number Lookup
      </h2>

      <form onSubmit={handleSubmit} className="mb-4">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <SafeIcon
              icon={FiPhone}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              placeholder="Enter phone number (e.g., 0744 155 921)"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <motion.button
            type="submit"
            disabled={isLoading || !phoneNumber.trim()}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? (
              <SafeIcon icon={FiClock} className="animate-spin" />
            ) : (
              <SafeIcon icon={FiSearch} />
            )}
            Search
          </motion.button>
        </div>
      </form>

      {searchResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4"
        >
          {searchResult.length > 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiUserCheck} className="text-green-500" />
                <h3 className="font-semibold text-green-800">Existing Client Found</h3>
              </div>
              {searchResult.map((lead) => (
                <div key={lead.id} className="bg-white rounded-lg p-3 mt-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><strong>Name:</strong> {lead.name}</div>
                    <div><strong>Phone:</strong> {lead.phone}</div>
                    <div><strong>Email:</strong> {lead.email}</div>
                    <div>
                      <strong>Status:</strong>
                      <span className={`ml-1 px-2 py-1 rounded-full text-xs ${
                        lead.status === 'converted' ? 'bg-green-100 text-green-800' :
                        lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {lead.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiUserX} className="text-blue-500" />
                <h3 className="font-semibold text-blue-800">New Lead</h3>
              </div>
              <p className="text-blue-700 mt-1">
                This phone number is not in our database. This is a new potential client!
              </p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default PhoneSearch;